/**
 * Brand Color System
 * Centralized color constants matching Tailwind configuration
 * Use these constants throughout the app for consistency
 */

export const BRAND_COLORS = {
  // Primary brand colors from Tailwind
  black: "#000000",
  white: "#FFFFFF",
  gray: "#AAAAAA",
  yellow: "#F2C744", // Main yellow/gold - Premium/Play/Energy
  blue: "#3E61FA", // Trust/Stability
  green: "#63DF77", // Active/Success/Growth (Henlo Green)
  red: "#FF4B48", // Destructive/Intense
  pink: "#FF72B2", // Playful creativity
  purple: "#C02EF0", // Creative/Artistic
  orange: "#FFA648", // Community/Social
} as const;

// Color usage by context/page
export const COLOR_CONTEXT = {
  // Navigation states
  nav: {
    active: BRAND_COLORS.green, // Active navigation
    default: BRAND_COLORS.blue, // Default navigation
    hover: BRAND_COLORS.yellow, // Hover state
  },

  // Page-specific colors
  pages: {
    vault: BRAND_COLORS.green, // Earn, Vault pages
    buy: BRAND_COLORS.blue, // Buy, Lock pages
    play: BRAND_COLORS.yellow, // Play sections
    community: BRAND_COLORS.orange, // Guts, Volume
    create: BRAND_COLORS.purple, // Create, Gallery
    burn: BRAND_COLORS.red, // Burn, Incinerator
    gallery: BRAND_COLORS.pink, // Art Gallery accents
  },

  // UI elements
  ui: {
    success: BRAND_COLORS.green,
    warning: BRAND_COLORS.yellow,
    error: BRAND_COLORS.red,
    info: BRAND_COLORS.blue,
  },
} as const;

// RGB values for rgba() usage
export const BRAND_COLORS_RGB = {
  black: { r: 0, g: 0, b: 0 },
  white: { r: 255, g: 255, b: 255 },
  gray: { r: 170, g: 170, b: 170 },
  yellow: { r: 242, g: 199, b: 68 },
  blue: { r: 62, g: 97, b: 250 },
  green: { r: 99, g: 223, b: 119 },
  red: { r: 255, g: 75, b: 72 },
  pink: { r: 255, g: 114, b: 178 },
  purple: { r: 192, g: 46, b: 240 },
  orange: { r: 255, g: 166, b: 72 },
} as const;

// Helper function to get rgba string
export const getRgba = (color: keyof typeof BRAND_COLORS_RGB, alpha: number = 1): string => {
  const rgb = BRAND_COLORS_RGB[color];
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

// Helper function to get connected 3D text shadow extrusion with border
export const getTextShadow = (color: string): string => {
  return `
    -1px -1px 0 ${color},
    1px -1px 0 ${color},
    -1px 1px 0 ${color},
    1px 1px 0 ${color},
    2px 2px 0 ${color},
    3px 3px 0 ${color},
    4px 4px 0 ${color},
    5px 5px 0 ${color},
    5px 5px 3px rgba(0, 0, 0, 0.3)
  `
    .replace(/\s+/g, " ")
    .trim();
};

// Button variant colors
export const BUTTON_VARIANTS = {
  "action-yellow": {
    from: BRAND_COLORS.yellow,
    to: "#D4A62E",
    text: BRAND_COLORS.black,
  },
  "action-green": {
    from: BRAND_COLORS.green,
    to: "#4FB85B",
    text: BRAND_COLORS.black,
  },
  "action-blue": {
    from: BRAND_COLORS.blue,
    to: "#2E4FD8",
    text: BRAND_COLORS.white,
  },
  "action-purple": {
    from: BRAND_COLORS.purple,
    to: "#7A3FD4",
    text: BRAND_COLORS.white,
  },
  "action-pink": {
    from: BRAND_COLORS.pink,
    to: "#E550A0",
    text: BRAND_COLORS.white,
  },
  "action-red": {
    from: BRAND_COLORS.red,
    to: "#D63330",
    text: BRAND_COLORS.white,
  },
  "action-orange": {
    from: BRAND_COLORS.orange,
    to: "#E68A3C",
    text: BRAND_COLORS.black,
  },
} as const;
